/**
 * The timeline strip — the first registered panel (ADR-015 §5) and the first
 * visual surface over the timeline slice (ADR-006 §13.4). READ-ONLY in this
 * seam: it renders only what the engine measured — the Document's authored
 * clips by name, the ACTIVE clip's key pips at their committed times, and the
 * live playback clock. Staging is the one gesture (a chip click →
 * store.selectClip — the §13.4 selection, already store-vocabulary). Scrub
 * and key-dragging land with the playhead-control seam; nothing here fakes
 * them (§13.0.2: ship it when its substrate ships, or don't show it).
 * A pure resolver: closing it destroys DOM, never data (§13.0.1).
 * Depends on: store (via mount), viewport probe (injected at registration —
 * the clock is render truth, not Document truth).
 * Depended on by: main (registration line only).
 */
import {
  authoredClipDurationSec,
  describeMaterialize,
  hasPlayableKeys,
  materializeClip,
  type ClipData,
} from '@harryhausen/kernel';
import type { PanelMount } from './panel-registry.js';
import type { ViewportProbe } from '../viewport/viewport.js';

/**
 * What the imported clip currently is, asked at click time rather than captured
 * at registration — the subject is replaceable once the import surface lands,
 * and a captured clip would go stale the moment it is.
 */
export type ImportedClipSource = () => ClipData | null;

/** Build the strip's mount. The probe rides the registration closure so the
 *  mount contract stays (container, store) — panels never reach for globals. */
export function createTimelineStrip(probe: ViewportProbe, importedClip: ImportedClipSource): PanelMount {
  return (container, store) => {
    container.classList.add('timeline-strip');

    const clipsRow = document.createElement('div');
    clipsRow.className = 'strip-clips';
    clipsRow.setAttribute('role', 'group');
    clipsRow.setAttribute('aria-label', 'Staged clip');

    const lane = document.createElement('div');
    lane.className = 'strip-lane';
    const playhead = document.createElement('div');
    playhead.className = 'strip-playhead';
    const clock = document.createElement('span');
    clock.className = 'strip-clock';
    clock.dataset['stripClock'] = '';

    // What the last materialize DID — including what it could not carry. A
    // rotation-only authoring silently drops a walk's forward travel, so the
    // report is a live line on the surface, not a console message nobody reads
    // (role=status so it reaches AT as well as eyes).
    const notice = document.createElement('p');
    notice.className = 'strip-notice';
    notice.setAttribute('role', 'status');
    notice.dataset['stripNotice'] = '';
    notice.hidden = true;
    let noticeText: string | null = null;
    let noticeKind: 'report' | 'refusal' = 'report';

    container.append(clipsRow, lane, clock, notice);

    /** Re-render chips + pips from the store — the resolver half. */
    const render = (): void => {
      const doc = store.getDocument();
      const active = store.getShell().activeClip;

      clipsRow.replaceChildren();
      const chip = (label: string, name: string | null, keyless: boolean): void => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'strip-chip';
        button.dataset['clipChip'] = name ?? '(default)';
        button.setAttribute('aria-pressed', String(active === name));
        button.textContent = keyless ? `${label} — no keys yet` : label;
        button.addEventListener('click', () => store.selectClip(name));
        clipsRow.appendChild(button);
      };
      chip('imported', null, false);

      // The one gesture that turns the imported clip into Document truth. Until
      // it runs, the retargeted animation plays through the mixer while the
      // Document knows nothing about it — no keys to draw, nothing for the
      // key.* verbs to edit, no single source for an exporter to read.
      const source = importedClip();
      const authorable = source === null ? null : source.name;
      const alreadyAuthored = authorable !== null && doc.clips.some((c) => c.name === authorable);
      const author = document.createElement('button');
      author.type = 'button';
      author.className = 'strip-author';
      author.dataset['authorKeys'] = '';
      author.textContent = 'Author keys';
      // A dead button reads as broken, so the DISABLED state carries the reason
      // — the clip name is an identity, and materializing twice would refuse.
      author.disabled = authorable === null || alreadyAuthored;
      author.title =
        authorable === null
          ? 'nothing imported in this session to author from'
          : alreadyAuthored
            ? `already authored as "${authorable}" — clip names are identities`
            : `author "${authorable}" into the Document as editable keys`;
      clipsRow.appendChild(author);
      author.addEventListener('click', () => {
        try {
          const clip = importedClip();
          if (clip === null) throw new Error('nothing imported in this session to author from');
          const { command, report } = materializeClip(clip);
          store.dispatch(command);
          noticeText = describeMaterialize(report);
          noticeKind = 'report';
          // Stage what was just authored: the point of the gesture is SEEING it.
          store.selectClip(report.clipName);
        } catch (error) {
          // Refusals are the surface's to show. The §8 message already names
          // what refused and what would release it.
          noticeText = error instanceof Error ? error.message : String(error);
          noticeKind = 'refusal';
          render();
        }
      });

      for (const authored of doc.clips) chip(authored.name, authored.name, !hasPlayableKeys(authored));

      notice.hidden = noticeText === null;
      notice.textContent = noticeText ?? '';
      notice.dataset['noticeKind'] = noticeKind;

      lane.replaceChildren(playhead);
      const staged = active === null ? undefined : doc.clips.find((c) => c.name === active);
      if (staged !== undefined && hasPlayableKeys(staged)) {
        // The lane spans exactly what the MIXER plays. Measuring it here with
        // a floor of our own drew a playhead wrapping out of phase with the
        // clip being sampled — worst at the commonest case, a single key at
        // 0s, where the mixer holds one frame (1/fps) while a 0.001s lane
        // wrapped ~33 times through it (Codex P2, #63). The strip renders
        // what the engine measured; it does not compute a second truth.
        const span = authoredClipDurationSec(staged, doc.fps.value);
        lane.dataset['laneSpanSec'] = String(span);
        for (const track of staged.tracks) {
          for (const key of track.keys) {
            const pip = document.createElement('span');
            pip.className = 'strip-pip';
            pip.dataset['keyId'] = key.id;
            pip.title = `${key.id} · ${track.bone} @ ${key.time}s`;
            pip.style.left = `${Math.min(100, (key.time / span) * 100)}%`;
            lane.appendChild(pip);
          }
        }
      } else {
        delete lane.dataset['laneSpanSec'];
      }
    };

    // The clock half: rAF-paced readout of the live probe. The playhead line
    // moves only when an authored clip is staged (its span is Document truth);
    // the numeric clock is always honest — it is the render loop's own count.
    let raf = 0;
    const tick = (): void => {
      clock.textContent = `t = ${probe.clipTimeSec.toFixed(2)}s`;
      const span = Number(lane.dataset['laneSpanSec'] ?? 0);
      if (span > 0) {
        playhead.style.display = '';
        playhead.style.left = `${((probe.clipTimeSec % span) / span) * 100}%`;
      } else {
        playhead.style.display = 'none';
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const unsubscribe = store.subscribe(render);
    render();

    return {
      dispose() {
        cancelAnimationFrame(raf);
        unsubscribe();
        container.replaceChildren();
        container.classList.remove('timeline-strip');
      },
    };
  };
}
